/**
 * SemanticClarityView — the cream/teal Semantic Clarity surface (v2.5).
 *
 * Originally a deliberately-shallow v2.0 summary (beat 6). As of v2.5 it is the
 * single, on-brand Semantic Clarity destination: the summary (score + coverage +
 * matched vocabularies) PLUS a field-level explorer that drills into which
 * columns mapped to which vocabulary (with confidence + match method) and which
 * did not. It replaces the legacy purple iframe explorer as the default surface;
 * the legacy page stays one click away for HITL accept/reject + Excel export.
 *
 * Two entry points share one body:
 *   - SemanticClarityView  — the /analysis/:job_id/semantic-clarity route (chrome
 *     + back button) — rendered for deep-link / Home access.
 *   - SemanticClarityPanel — embedded inline in the journey's Semantic Clarity
 *     (Ontology Inference) station tab.
 *
 * Data: GET /api/ontology-inference/results/:job_id — the same persisted
 * Station-6 result the legacy deep view reads, so the numbers always agree.
 */
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useOntologyInferenceResults,
  type OntologyMappingRecord,
  type UnmappedColumnRecord,
} from '../hooks/useOntologyInferenceResults';
import './semantic-clarity.css';

type MaturityBand = 'strong' | 'partial' | 'emerging' | 'none';

function maturityOf(score: number): MaturityBand {
  if (score >= 80) return 'strong';
  if (score >= 50) return 'partial';
  if (score > 0) return 'emerging';
  return 'none';
}

interface VocabGroup {
  name: string;
  fields: number;
}

function groupVocabularies(mappings: OntologyMappingRecord[]): VocabGroup[] {
  const counts = new Map<string, number>();
  for (const m of mappings) {
    const name = m.ontology_name || m.ontology_code || 'Unnamed vocabulary';
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, fields]) => ({ name, fields }))
    .sort((a, b) => b.fields - a.fields);
}

/** Confidence is persisted as a 0–1 fraction (older rows) or 0–100 — normalize. */
function pctConfidence(confidence: number): number {
  if (!Number.isFinite(confidence)) return 0;
  return Math.round(confidence <= 1 ? confidence * 100 : confidence);
}

/** Deterministic verdict copy keyed on the maturity band — no AI call. */
function bandHeadline(
  band: MaturityBand,
  topVocab: VocabGroup | undefined,
  unmappedCount: number,
): { lead: string; rest: string } {
  switch (band) {
    case 'strong':
      return {
        lead: 'Most of the schema maps to shared concepts.',
        rest: topVocab
          ? `${topVocab.name} anchors ${topVocab.fields} fields; ${
              unmappedCount > 0
                ? `${unmappedCount} ${unmappedCount === 1 ? 'column still carries' : 'columns still carry'} no semantic anchor.`
                : 'every profiled column carries a semantic anchor.'
            }`
          : 'Nearly every profiled column carries a semantic anchor.',
      };
    case 'partial':
      return {
        lead: 'Half the schema maps to known concepts; the rest is locally defined.',
        rest: topVocab
          ? `${topVocab.name} anchors ${topVocab.fields} fields, but ${unmappedCount} columns carry no semantic anchor.`
          : `${unmappedCount} columns carry no semantic anchor.`,
      };
    case 'emerging':
      return {
        lead: 'Only a fraction of the schema carries shared meaning.',
        rest: `Most columns are locally defined — ${unmappedCount} carry no semantic anchor.`,
      };
    case 'none':
      return {
        lead: 'No columns map to shared vocabulary yet.',
        rest: 'The schema is entirely locally defined — semantic alignment starts from zero.',
      };
  }
}

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const WarnIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);

const ChevronIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const UNMAPPED_SHOWN = 3;
type ExplorerTab = 'mapped' | 'unmapped' | 'all';

// ============================================================================
// PANEL (shared body — route chrome and the station tab both render this)
// ============================================================================

export const SemanticClarityPanel: React.FC<{ jobId: string | undefined; journeyId?: string }> = ({
  jobId,
  journeyId,
}) => {
  const { data, loading, error } = useOntologyInferenceResults(jobId);

  // Explorer state
  const [tab, setTab] = useState<ExplorerTab>('mapped');
  const [vocabFilter, setVocabFilter] = useState<string | null>(null);
  const [expandedCol, setExpandedCol] = useState<string | null>(null);

  const model = useMemo(() => {
    if (!data) return null;
    const r = data.result;
    const total = r.total_columns ?? 0;
    const mapped = r.mapped_columns ?? 0;
    const unmappedCount = r.unmapped_columns ?? 0;
    const score = total > 0 ? Math.round((mapped / total) * 100) : 0;
    const band = maturityOf(score);
    const mappings = r.mappings ?? [];
    const unmapped = r.unmapped ?? [];
    const vocabGroups = groupVocabularies(mappings);
    const unmappedShown: UnmappedColumnRecord[] = unmapped.slice(0, UNMAPPED_SHOWN);
    const unmappedMore = Math.max(0, unmapped.length - UNMAPPED_SHOWN);
    const headline = bandHeadline(band, vocabGroups[0], unmappedCount);
    // Older rows can carry counters without per-field mappings — counters win.
    const mappingsUnavailable = mapped > 0 && mappings.length === 0;
    return {
      total, mapped, unmappedCount, score, band, vocabGroups, mappings, unmapped,
      unmappedShown, unmappedMore, headline, mappingsUnavailable,
    };
  }, [data]);

  // ---- shell states -------------------------------------------------------

  if (loading) {
    return <p className="scv-muted" role="status">Loading semantic clarity&hellip;</p>;
  }

  if (error || !data || !model) {
    const notFound = !!error && error.includes('HTTP 404');
    return (
      <div className="scv-error" role="alert">
        {notFound
          ? 'No ontology inference results found for this analysis. Run the Ontology Inference station on this journey, then return here.'
          : `Failed to load semantic clarity${error ? `: ${error}` : '.'}`}
      </div>
    );
  }

  const {
    total, mapped, unmappedCount, score, band, vocabGroups, mappings, unmapped,
    unmappedShown, unmappedMore, headline, mappingsUnavailable,
  } = model;
  const ringCircumference = 2 * Math.PI * 52;
  const ringFill = (score / 100) * ringCircumference;

  const drillTo = (next: ExplorerTab, vocab: string | null) => {
    setTab(next);
    setVocabFilter(vocab);
    setExpandedCol(null);
    // Reveal the explorer below the summary. Guarded: jsdom (tests) doesn't
    // implement scrollIntoView and throws.
    try {
      document.getElementById('scv-explorer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      /* no-op */
    }
  };

  // Rows for the active explorer tab.
  const mappedRows = (vocabFilter
    ? mappings.filter((m) => (m.ontology_name || m.ontology_code) === vocabFilter)
    : mappings
  ).slice().sort((a, b) => a.column_index - b.column_index);
  const unmappedRows = unmapped.slice().sort((a, b) => a.column_index - b.column_index);

  const legacyHref = `/ontology-inference-results-v2.html?job_id=${encodeURIComponent(jobId ?? '')}${
    journeyId ? `&journey_id=${encodeURIComponent(journeyId)}` : ''
  }`;

  return (
    <>
      {/* verdict band */}
      <div className="scv-band">
        <div className="scv-hero">
          <div className="scv-ring" role="img" aria-label={`Semantic clarity score ${score} of 100`}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="52" fill="none" className="scv-ring-track" strokeWidth="10" />
              <circle
                cx="60" cy="60" r="52" fill="none" className="scv-ring-fill" strokeWidth="10"
                strokeLinecap="round" strokeDasharray={`${ringFill} ${ringCircumference}`}
              />
            </svg>
            <div className="scv-ring-val">
              <span className="scv-ring-num">{score}</span>
              <span className="scv-ring-den">/ 100</span>
            </div>
          </div>
          <div>
            <div className="scv-band-eyebrow">Semantic maturity &mdash; {band}</div>
            <p className="scv-band-headline">
              <b>{headline.lead}</b> {headline.rest}
            </p>
            <p className="scv-band-sub">
              {mapped} of {total} columns mapped &middot; {vocabGroups.length} candidate{' '}
              {vocabGroups.length === 1 ? 'ontology' : 'ontologies'} matched &middot; {unmappedCount}{' '}
              {unmappedCount === 1 ? 'column' : 'columns'} unmapped.
            </p>
          </div>
        </div>
      </div>

      {/* summary cards */}
      <div className="scv-cards">
        <div className="scv-card">
          <h2>Coverage</h2>
          <div className="scv-stat">
            {mapped}
            <small> / {total} columns mapped</small>
          </div>
          <div className="scv-bar-track">
            <div className="scv-bar-fill" style={{ width: `${score}%` }} />
          </div>
          <p className="scv-note">
            {total === 0
              ? 'No columns were profiled in this run.'
              : unmappedShown.length > 0
                ? <>The gap is in {unmappedShown.map((u) => <code key={u.column_name}>{u.column_name}</code>).reduce<React.ReactNode[]>((acc, el, i) => (i === 0 ? [el] : [...acc, ', ', el]), [])}{unmappedMore > 0 ? ` and ${unmappedMore} more` : ''} &mdash; fields with no match in the searched vocabularies.</>
                : unmappedCount > 0
                  ? `${unmappedCount} ${unmappedCount === 1 ? 'column has' : 'columns have'} no vocabulary match.`
                  : 'Every profiled column maps to at least one shared vocabulary.'}
          </p>
          {unmappedCount > 0 && (
            <button type="button" className="scv-drill-link" onClick={() => drillTo('unmapped', null)}>
              See all {unmappedCount} unmapped columns <ChevronIcon />
            </button>
          )}
        </div>

        <div className="scv-card">
          <h2>Matched vocabularies</h2>
          {mappingsUnavailable ? (
            <p className="scv-note">
              Per-field mappings are unavailable for this run &mdash; the coverage counters above are the
              source of truth.
            </p>
          ) : (
            <>
              <ul className="scv-vocab">
                {vocabGroups.map((v) => (
                  <li key={v.name}>
                    <button
                      type="button"
                      className="scv-vocab-row"
                      onClick={() => drillTo('mapped', v.name)}
                      aria-label={`Show the ${v.fields} fields mapped to ${v.name}`}
                    >
                      <CheckIcon /> <span className="scv-vocab-name">{v.name}</span>
                      <span className="scv-src">
                        {v.fields} {v.fields === 1 ? 'field' : 'fields'}
                      </span>
                      <ChevronIcon />
                    </button>
                  </li>
                ))}
                {unmappedShown.map((u) => (
                  <li className="is-warn" key={u.column_name}>
                    <button type="button" className="scv-vocab-row" onClick={() => drillTo('unmapped', null)}>
                      <WarnIcon /> <span className="scv-vocab-name">{u.column_name} &mdash; no match</span>
                      <span className="scv-src">unmapped</span>
                      <ChevronIcon />
                    </button>
                  </li>
                ))}
                {vocabGroups.length === 0 && unmappedShown.length === 0 && (
                  <li><WarnIcon /> No vocabulary matches recorded for this run.</li>
                )}
              </ul>
              {unmappedMore > 0 && (
                <button type="button" className="scv-drill-link" onClick={() => drillTo('unmapped', null)}>
                  + {unmappedMore} more unmapped <ChevronIcon />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* field-level explorer */}
      {!mappingsUnavailable && (
        <section className="scv-explorer" id="scv-explorer" aria-label="Field-level mapping">
          <div className="scv-explorer-head">
            <h2>Field-level mapping</h2>
            <a className="scv-explorer-legacy" href={legacyHref} target="_blank" rel="noopener noreferrer">
              Open full explorer &amp; feedback &rarr;
            </a>
          </div>

          <div className="scv-tabs" role="tablist" aria-label="Filter columns">
            {([
              ['mapped', 'Mapped', mapped],
              ['unmapped', 'Unmapped', unmappedCount],
              ['all', 'All columns', total],
            ] as [ExplorerTab, string, number][]).map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`scv-tab ${tab === id ? 'is-active' : ''}`}
                onClick={() => { setTab(id); setExpandedCol(null); if (id !== 'mapped') setVocabFilter(null); }}
              >
                {label} <span className="scv-tab-count">{count}</span>
              </button>
            ))}
          </div>

          {tab === 'mapped' && vocabFilter && (
            <div className="scv-filter">
              Showing fields mapped to <b>{vocabFilter}</b>
              <button type="button" className="scv-filter-clear" onClick={() => setVocabFilter(null)}>clear</button>
            </div>
          )}

          <ul className="scv-rows">
            {(tab === 'unmapped' ? [] : mappedRows).map((m) => (
              <MappedRow
                key={`m-${m.column_index}-${m.column_name}`}
                m={m}
                expanded={expandedCol === `m:${m.column_name}`}
                onToggle={() => setExpandedCol(expandedCol === `m:${m.column_name}` ? null : `m:${m.column_name}`)}
              />
            ))}
            {(tab === 'mapped' ? [] : unmappedRows).map((u) => (
              <UnmappedRow
                key={`u-${u.column_index}-${u.column_name}`}
                u={u}
                expanded={expandedCol === `u:${u.column_name}`}
                onToggle={() => setExpandedCol(expandedCol === `u:${u.column_name}` ? null : `u:${u.column_name}`)}
              />
            ))}
            {tab === 'mapped' && mappedRows.length === 0 && (
              <li className="scv-row-empty">No mapped columns{vocabFilter ? ` for ${vocabFilter}` : ''}.</li>
            )}
            {tab === 'unmapped' && unmappedRows.length === 0 && (
              <li className="scv-row-empty">Every profiled column maps to a vocabulary.</li>
            )}
          </ul>
        </section>
      )}
    </>
  );
};

// --- explorer rows ----------------------------------------------------------

const MappedRow: React.FC<{ m: OntologyMappingRecord; expanded: boolean; onToggle: () => void }> = ({
  m, expanded, onToggle,
}) => {
  const vocab = m.ontology_name || m.ontology_code || 'Unnamed vocabulary';
  return (
    <li className="scv-row is-mapped">
      <button type="button" className="scv-row-head" aria-expanded={expanded} onClick={onToggle}>
        <span className={`scv-row-chev ${expanded ? 'is-open' : ''}`}><ChevronIcon /></span>
        <code className="scv-row-col">{m.column_name}</code>
        <span className="scv-row-vocab">{vocab}</span>
        <span className="scv-row-conf">{pctConfidence(m.confidence)}%</span>
      </button>
      {expanded && (
        <dl className="scv-row-detail">
          <div><dt>Vocabulary</dt><dd>{vocab}{m.ontology_code ? ` (${m.ontology_code})` : ''}</dd></div>
          <div><dt>Confidence</dt><dd>{pctConfidence(m.confidence)}%</dd></div>
          {m.match_method && <div><dt>Match method</dt><dd>{m.match_method}</dd></div>}
          {m.ontology_description && <div><dt>Concept</dt><dd>{m.ontology_description}</dd></div>}
          {m.ontology_iri && <div><dt>IRI</dt><dd><span className="scv-row-iri">{m.ontology_iri}</span></dd></div>}
        </dl>
      )}
    </li>
  );
};

const UnmappedRow: React.FC<{ u: UnmappedColumnRecord; expanded: boolean; onToggle: () => void }> = ({
  u, expanded, onToggle,
}) => (
  <li className="scv-row is-unmapped">
    <button type="button" className="scv-row-head" aria-expanded={expanded} onClick={onToggle}>
      <span className={`scv-row-chev ${expanded ? 'is-open' : ''}`}><ChevronIcon /></span>
      <code className="scv-row-col">{u.column_name}</code>
      <span className="scv-row-vocab is-none">no match</span>
      <span className="scv-row-conf">&mdash;</span>
    </button>
    {expanded && (
      <dl className="scv-row-detail">
        <div><dt>Status</dt><dd>No vocabulary match in the searched ontologies.</dd></div>
        {u.reason && <div><dt>Reason</dt><dd>{u.reason}</dd></div>}
      </dl>
    )}
  </li>
);

// ============================================================================
// ROUTE (chrome + back button)
// ============================================================================

export const SemanticClarityView: React.FC = () => {
  const { job_id } = useParams<{ job_id: string }>();
  const navigate = useNavigate();

  return (
    <div className="scv-page">
      <div className="scv-inner">
        <button type="button" className="scv-back" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="scv-eyebrow">Semantic Clarity &middot; Ontology Coverage</div>
        <h1 className="scv-headline">Semantic Clarity</h1>
        <p className="scv-subhead">
          How well the data&rsquo;s meaning is pinned to shared vocabulary &mdash; the headline score, what to
          align next, and the field-level mapping of every column.
        </p>

        <SemanticClarityPanel jobId={job_id} />
      </div>
    </div>
  );
};

export default SemanticClarityView;
